import { DateUtils } from 'submodules/nerit-framework-utils/utils/date/DateUtils'
import { DateFormatEnum } from 'submodules/nerit-framework-utils/utils/enums/DateFormatEnum'
import React from 'react'
import { DataAnalysisListItemResponseDTO } from 'submodules/nerit-aquatec-sdk/services/data-analysis/dtos/response/DataAnalysisListItemResponseDTO'
import { ColumnProps } from 'antd/lib/table'
import styled from 'styled-components'
import { FieldValueResponseDTO } from 'submodules/nerit-aquatec-sdk/services/data-analysis/dtos/response/FieldValueResponseDTO'
import { PermissionUtils } from 'app/components/access-control/inner/PermissionUtils'
import { PermissionEnum } from 'submodules/nerit-aquatec-sdk/services/auth/authenticate/enums/PermissionEnum'
import { TooltipCP } from 'submodules/nerit-framework-ui/common/components/tooltip/TooltipCP'
import { RegulationConditionUtils } from 'modules/fields/utils/RegulationConditionUtils'
import { ConditionalLimitLabelCP } from 'modules/fields/components/conditional-limit-label/ConditionalLimitLabelCP'

type TableStructureTypeTP = { columns: ColumnProps<DataAnalysisListItemResponseDTO>[], data: any[] }

interface LocationData {
    code: number
    name: string
    dates: Set<string>
    values: Map<string, FieldValueResponseDTO[]>  // key: date, value: array of field values
}

/**
 */
export class TableDataAnalysisUtils {

    /**
     */
    static mountTableStructure(
        dataAnalysData: DataAnalysisListItemResponseDTO[],
        onClick: (data: FieldValueResponseDTO) => void,
    ): TableStructureTypeTP {
        // 1. First create the fixed columns
        const tableColumns: ColumnProps<DataAnalysisListItemResponseDTO>[] = [
            { title: 'Parâmetro', dataIndex: 'field', className: 'fixedColumn col1', width: 150 },
            {
                title: 'Limite',
                dataIndex: 'limit',
                className: 'fixedColumn col3',
                width: 100,
                render: (text: string, record: any) => !!record.limitReferenceName
                    ? <ConditionalLimitLabelCP referenceName={record.limitReferenceName} conditions={record.limitConditions}/>
                    : text,
            },
        ]

        // 2. Organize location data
        const locationMap = new Map<number, LocationData>()
        
        // Process all data to organize by location
        dataAnalysData.forEach(record => {
            record.locations.forEach(loc => {
                let locationData = locationMap.get(loc.location.code)
                if (!locationData) {
                    locationData = {
                        code: loc.location.code,
                        name: loc.location.name,
                        dates: new Set<string>(),
                        values: new Map<string, FieldValueResponseDTO[]>()
                    }
                    locationMap.set(loc.location.code, locationData)
                }

                // Add all dates and values for this location
                loc.values.forEach(value => {
                    locationData!.dates.add(value.creationDate)
                    
                    // Get existing values for this date or create new array
                    const existingValues = locationData!.values.get(value.creationDate) || []
                    existingValues.push(value)
                    locationData!.values.set(value.creationDate, existingValues)
                })
            })
        })

        // 3. Create location columns with all their dates
        let locationIndex = 0
        Array.from(locationMap.values()).sort((a,b) => (a.name).localeCompare((b.name),'pt-BR')).forEach((locationData) => {
            const locationColumn: ColumnProps<DataAnalysisListItemResponseDTO> = {
                title: locationData.name,
                key: locationData.code.toString(),
                className: `aqt-class-col-${locationIndex % 2}`,
                children: Array.from(locationData.dates).sort().map(date => ({
                    title: DateUtils.formatDate(date, DateFormatEnum.BR_WITHOUT_TIME),
                    dataIndex: `${locationData.code}_${date}`,
                    key: `${locationData.code}_${date}`,
                    className: `aqt-class-col-${locationIndex % 2}`,
                    render: (text: string, record: any) => {
                        if (!text) return null

                        let fieldValues: FieldValueResponseDTO[] = []
                        try {
                            const parsed = JSON.parse(text)
                            // Check if it's an array or single value
                            fieldValues = Array.isArray(parsed) ? parsed : [parsed]
                        } catch (e) {}

                        if (!fieldValues || fieldValues.length === 0) return null

                        // If only one value, render it normally
                        if (fieldValues.length === 1) {
                            const fieldVal = fieldValues[0]
                            return {
                                children: TableDataAnalysisUtils.withAppliedLimitTooltip(fieldVal, record.limitReferenceName, (
                                    <ValueSCP
                                        hasPermission={PermissionUtils.canAccess(PermissionEnum.ROLE_ADMIN)}
                                        className={fieldVal.isOutsidePermittedLimits ? 'aqt-error-value' : ''}
                                        onClick={() => {
                                            if (PermissionUtils.canAccess(PermissionEnum.ROLE_ADMIN)) {
                                                onClick(fieldVal)
                                            }
                                        }}
                                    >
                                        {fieldVal.originalValue}
                                    </ValueSCP>
                                ))
                            }
                        }

                        // If multiple values, render them in a container
                        return {
                            children: (
                                <ValuesContainerSCP>
                                    {fieldValues.map((fieldVal, index) => TableDataAnalysisUtils.withAppliedLimitTooltip(fieldVal, record.limitReferenceName, (
                                        <ValueSCP
                                            hasPermission={PermissionUtils.canAccess(PermissionEnum.ROLE_ADMIN)}
                                            className={fieldVal.isOutsidePermittedLimits ? 'aqt-error-value' : ''}
                                            onClick={() => {
                                                if (PermissionUtils.canAccess(PermissionEnum.ROLE_ADMIN)) {
                                                    onClick(fieldVal)
                                                }
                                            }}
                                        >
                                            {fieldVal.originalValue}
                                        </ValueSCP>
                                    ), index))}
                                </ValuesContainerSCP>
                            )
                        }
                    }
                }))
            }
            tableColumns.push(locationColumn)
            locationIndex++
        })

        // 4. Create table data
        const tableData = dataAnalysData.map((record, index) => {
            const rowData: any = {
                key: index,
                field: `${record.field.field.name} (${record.field.unity})`,
                limit: record.field.regulationMinPermitted && record.field.regulationMaxPermitted 
                    ? `${record.field.regulationMinPermitted} | ${record.field.regulationMaxPermitted}`
                    : record.field.regulationMinPermitted
                        ? record.field.regulationMinPermitted
                        : record.field.regulationMaxPermitted
                            ? record.field.regulationMaxPermitted 
                            : '',
            }

            // Norma condicional: a coluna Limite mostra "Condicional (pH)" e cada valor, o limite aplicado.
            if (!!record.field.regulationReferenceFieldName) {
                rowData.limit = RegulationConditionUtils.conditionalLabel(record.field.regulationReferenceFieldName)
                rowData.limitReferenceName = record.field.regulationReferenceFieldName
                rowData.limitConditions = record.field.regulationConditions ?? []
            }

            // Add values for each location and date
            record.locations.forEach(loc => {
                // Group values by date for this location and parameter
                const valuesByDate = new Map<string, FieldValueResponseDTO[]>()
                
                loc.values.forEach(value => {
                    const existingValues = valuesByDate.get(value.creationDate) || []
                    existingValues.push(value)
                    valuesByDate.set(value.creationDate, existingValues)
                })

                // Add grouped values to row data
                valuesByDate.forEach((values, date) => {
                    // Only group if there are multiple values, otherwise use single value
                    if (values.length === 1) {
                        rowData[`${loc.location.code}_${date}`] = JSON.stringify(values[0])
                    } else {
                        rowData[`${loc.location.code}_${date}`] = JSON.stringify(values)
                    }
                })
            })

            return rowData
        })

        return {
            columns: tableColumns,
            data: tableData
        }
    }

    /** Com norma condicional, o valor ganha tooltip com o limite aplicado ou "Sem limite aplicável". */
    static withAppliedLimitTooltip(fieldValue: FieldValueResponseDTO, referenceName: string | undefined, element: JSX.Element, key?: number): JSX.Element {

        if (!referenceName)
            return React.cloneElement(element, { key })

        return (
            <TooltipCP key={key} showSpan={true} text={RegulationConditionUtils.appliedLimitText(fieldValue, referenceName)}>
                {element}
            </TooltipCP>
        )
    }

    static getLocationValueDataIndex(locationCode: number, creationDate: string): string {
        return `${locationCode}${creationDate}`
    }

}

const ValueSCP = styled.div<{ hasPermission: boolean }>`
    cursor: ${props => props.hasPermission ? 'pointer' : 'normal'};
    
    &.aqt-error-value {
        color: #ff4d4f;
    }
`

const ValuesContainerSCP = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-height: 20px;
    
    ${ValueSCP} {
        padding: 2px 4px;
        margin: 1px 0;
        border-radius: 3px;
        background-color: #f5f5f5;
        font-size: 0.9em;
        
        &:hover {
            background-color: #e6f7ff;
        }
        
        &.aqt-error-value {
            background-color: #fff2f0;
            color: #ff4d4f;
        }
    }
`
